"use client";

import {
  ArrowLeftRight,
  ArrowRight,
  Banknote,
  BookOpenCheck,
  Building2,
  FileText,
  HandCoins,
  Handshake,
  Landmark,
  PiggyBank,
  Scale,
  Send,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { TextCardGridComponent, TextCardGridItem } from "../../types";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { useLocale } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/** Icônes utilisables depuis le CMS pour un panneau de répertoire ou ses liens. */
const DIRECTORY_ICONS: Record<string, LucideIcon> = {
  ArrowLeftRight,
  Banknote,
  BookOpenCheck,
  Building2,
  FileText,
  HandCoins,
  Handshake,
  Landmark,
  PiggyBank,
  Scale,
  Send,
  ShieldCheck,
  Smartphone,
  TrendingUp,
};

/** Données d'un panneau de répertoire, quelle qu'en soit la provenance. */
type DirectoryGroup = {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  linkLabel: string;
  entriesEyebrow: string;
  entriesTitle: string;
  entries: Array<{ title: string; text: string; href: string; icon: LucideIcon }>;
};

/**
 * Panneau de répertoire : bandeau bleu (titre, description, lien) à gauche et
 * liste de liens à droite. Rendu UNIQUE partagé par toutes les pages de rubrique,
 * alimenté soit par le contenu CMS (`items[].entries`), soit par les groupes
 * historiques codés en dur — les deux passent par ce même composant pour que la
 * présentation ne puisse plus diverger d'une page à l'autre.
 */
function DirectoryPanel({ group }: { group: DirectoryGroup }) {
  const GroupIcon = group.icon;
  return (
    <article className="overflow-hidden rounded-md border border-bcc-panel shadow-bcc-panel bg-white lg:grid lg:grid-cols-[minmax(300px,0.82fr)_minmax(0,1.55fr)]">
      <div className="relative overflow-hidden bg-[#0769AC] p-7 text-white md:p-8">
        <GroupIcon className="pointer-events-none absolute -bottom-12 -right-10 h-52 w-52 text-white/[0.065]" strokeWidth={1.05} aria-hidden="true" />
        <div className="relative flex h-full flex-col">
          <div className="flex items-center gap-3">
            <GroupIcon className="h-7 w-7 shrink-0 text-white" strokeWidth={1.7} aria-hidden="true" />
            <h3 className="text-2xl font-bold leading-tight text-white md:text-[1.7rem]" style={{ fontFamily: "var(--font-aleo)" }}>
              {group.title}
            </h3>
          </div>
          {group.description ? (
            <p className="mt-3 max-w-lg text-[0.98rem] leading-7 text-blue-50/95">{group.description}</p>
          ) : null}
          {group.href ? (
            <GuardedLink href={group.href} label={group.title} fallback="unwrap" className="mt-6 inline-flex w-fit items-center gap-2 text-sm font-bold text-white">
              {group.linkLabel}
              <ArrowRight className="h-4 w-4 text-[#FFB323]" aria-hidden="true" />
            </GuardedLink>
          ) : null}
        </div>
      </div>

      <div className="p-6 md:p-7 lg:p-8">
        <header className="border-b border-[#DCE6ED] pb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#E98A00]">{group.entriesEyebrow}</p>
          <h4 className="mt-1 text-xl font-bold text-[#005CA9]" style={{ fontFamily: "var(--font-aleo)" }}>
            {group.entriesTitle}
          </h4>
        </header>
        <div className="grid sm:grid-cols-2 sm:gap-x-7">
          {group.entries.map((entry) => {
            const EntryIcon = entry.icon;
            // Une entrée sans destination reste VISIBLE (c'est une information : un
            // texte de loi cité dont le document n'est pas encore publié) mais elle
            // ne doit pas se donner l'allure d'un lien. D'où la version sans flèche
            // ni survol, servie par `unwrapChildren` : même règle que la liste des
            // textes de loi, où un document non connecté perd son icône.
            const corps = (fleche: boolean) => (
              <>
                <EntryIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#0069B4]" strokeWidth={1.8} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className={`block font-bold leading-5 text-[#005CA9] ${fleche ? "group-hover:underline" : ""}`}>{entry.title}</span>
                  {entry.text ? <span className="mt-1 block text-sm leading-5 text-slate-600">{entry.text}</span> : null}
                </span>
                {fleche ? (
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-[#FF9F00] transition-transform group-hover:translate-x-1" aria-hidden="true" />
                ) : null}
              </>
            );
            const rangee = "flex min-w-0 items-start gap-3 border-b border-[#E5EDF2] py-4 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0";
            return (
              <GuardedLink
                key={`${entry.href}-${entry.title}`}
                href={entry.href}
                label={entry.title}
                block
                fallback="unwrap"
                className={`group ${rangee}`}
                unwrapClassName={rangee}
                unwrapChildren={corps(false)}
              >
                {corps(true)}
              </GuardedLink>
            );
          })}
        </div>
      </div>
    </article>
  );
}

const COLS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 3;
}

function resolveCardIcon(item: TextCardGridItem): LucideIcon {
  const target = `${resolveLinkHref(item.href) ?? ""} ${item.title}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

  if (target.includes("microfinance")) return HandCoins;
  if (target.includes("stabilite")) return ShieldCheck;
  if (target.includes("inflation")) return TrendingUp;
  if (target.includes("numeraire")) return Banknote;
  if (target.includes("cooperative")) return Handshake;
  if (target.includes("caisse") || target.includes("epargne")) return PiggyBank;
  if (target.includes("messagerie")) return Send;
  if (target.includes("monnaie electronique")) return Smartphone;
  if (target.includes("bureau de change") || target.includes("forex")) return Banknote;
  if (target.includes("banque")) return Landmark;
  if (target.includes("institution financiere") || target.includes("etablissement")) return Building2;
  if (target.includes("societe financiere")) return Building2;
  if (target.includes("intermediaire")) return Building2;
  if (target.includes("rapport") || target.includes("bulletin") || target.includes("note")) return FileText;
  if (target.includes("publication") || target.includes("etude")) return BookOpenCheck;
  if (target.includes("reglement") || target.includes("legal")) return Scale;
  return FileText;
}

/** Navigation grid shared by institutional landing pages. */
export function TextCardGridRenderer({ component }: { component: TextCardGridComponent }) {
  const locale = useLocale();
  const english = locale.toLowerCase().startsWith("en");
  const { items, cardsPerRowTablet, cardsPerRowDesktop } = component.content;
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  const gridCols = `sm:grid-cols-2 md:${COLS[tablet]} lg:${COLS[desktop]}`;
  // Chemin PILOTÉ PAR LE CONTENU : dès qu'une carte porte des `entries`, la grille
  // devient une pile de panneaux de répertoire. C'est la voie à utiliser pour toute
  // nouvelle rubrique — aucun code à ajouter ici, tout vient du CMS.
  const dataDrivenGroups = items.filter((item) => (item.entries?.length ?? 0) > 0);
  if (dataDrivenGroups.length > 0) {
    return (
      <section className="space-y-7">
        {dataDrivenGroups.map((item, index) => (
          <DirectoryPanel
            key={item.title || `panel-${index}`}
            group={{
              title: item.title,
              description: item.text ?? "",
              href: resolveLinkHref(item.href) ?? "",
              icon: (item.icon && DIRECTORY_ICONS[item.icon]) || resolveCardIcon(item),
              linkLabel: item.linkLabel ?? (english ? "View all" : "Tout consulter"),
              entriesEyebrow: item.entriesEyebrow ?? (english ? "Official directory" : "Répertoire officiel"),
              entriesTitle: item.entriesTitle ?? (english ? "Services and registers" : "Services et registres"),
              entries: (item.entries ?? []).map((entry) => ({
                title: entry.title,
                text: entry.text ?? "",
                href: resolveLinkHref(entry.href) ?? "",
                icon:
                  (entry.icon && DIRECTORY_ICONS[entry.icon]) ||
                  resolveCardIcon({ title: entry.title, text: entry.text ?? "", href: entry.href }),
              })),
            }}
          />
        ))}
      </section>
    );
  }


  return (
    <section>
      <div className={`grid gap-4 ${gridCols}`}>
        {items.map((item, idx) => {
          const Icon = resolveCardIcon(item);
          const itemHref = resolveLinkHref(item.href) ?? "";
          const isRegulationTopic = itemHref.includes("/intermediaires-financiers/reglementation/");
          const hasDescription = Boolean(item.text?.trim()) && !isRegulationTopic;
          const hasLink = Boolean(resolveLinkHref(item.href));
          const card = (
            <div
              className={`group flex h-full flex-col rounded-lg bg-[#076FB2] p-6 text-white ring-1 ring-black/5 transition hover:bg-[#0567A7] md:p-7 ${
                hasDescription && hasLink ? "min-h-[220px]" : hasDescription ? "min-h-[150px]" : "min-h-[180px]"
              } ${
                hasDescription ? "items-stretch text-left" : "items-center text-center"
              }`}
            >
              {hasDescription ? (
                // Sur la plage tablette les colonnes sont trop étroites pour
                // l'icône EN LIGNE avec un titre long : elle passe au-dessus.
                // Mobile (une colonne large) et desktop gardent la rangée.
                <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-2.5 lg:flex-row lg:items-center lg:gap-3">
                  <Icon className="h-6 w-6 shrink-0 text-white" strokeWidth={1.8} aria-hidden="true" />
                  <h3 className="text-xl font-bold leading-7">{item.title}</h3>
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center">
                  <Icon className="h-9 w-9 shrink-0 text-white" strokeWidth={1.65} aria-hidden="true" />
                  <h3 className="mt-4 text-xl font-semibold leading-7">{item.title}</h3>
                </div>
              )}
              {hasDescription ? (
                <p className="mt-4 flex-1 text-[0.95rem] leading-6 text-white/95">{item.text}</p>
              ) : null}
              {item.href ? (
                <span className="mt-5 inline-flex items-center gap-3 text-sm font-bold text-white">
                  {english ? "Read more" : "Lire la suite"}
                  <ArrowRight className="h-4 w-4 text-[#FFA401] transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              ) : null}
            </div>
          );

          if (item.href) {
            return (
              <GuardedLink
                key={item.title + String(idx)}
                href={item.href}
                label={item.title}
                block
                fallback="unwrap"
                className="block h-full"
              >
                {card}
              </GuardedLink>
            );
          }
          return <div key={item.title + String(idx)}>{card}</div>;
        })}
      </div>
    </section>
  );
}
